import { useMemo } from 'react';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useCustomizationContext } from './useCardCustomization';
import { useNavigationContext } from './useNavigationState';
import type { StationeryTemplateCategoryEnum } from '@graphql/generated';
import { useSalesConfig } from './useSalesConfig';
import { isPromoValid } from '@shared/utils/promotions';
import type { PromotionConfig } from '@shared/utils/promotions';

export type PromotionVendor = 'paperlust' | 'taylor' | 'joy_digital';

// Re-export types and functions from shared module for backwards compatibility
export { isCodeExpired, isPromoValid, isPromoValidForVendor, doesProductComponentMatch } from '@shared/utils/promotions';
export type { PromotionConfig, ProductInfo } from '@shared/utils/promotions';

// Just gets all promotions.
// Not restricted to Taylor only contexts.
export const usePromotionList = (category: StationeryTemplateCategoryEnum) => {
  const { value, payload } = useFeatureValue('printCouponCodeLaunchV2');
  const { sales, isEnabled: salesEnabled } = useSalesConfig(category);

  const coupons: PromotionConfig[] | null = payload instanceof Array ? payload : null;
  const couponsAndSales = useMemo(() => {
    return [...(sales && salesEnabled ? sales : []), ...(coupons ? coupons : [])];
  }, [coupons, sales, salesEnabled]);

  return {
    isEnabled: value === 'on',
    promotions: couponsAndSales,
  };
};

// Gets current promotion from CardCustomizer saved promo and validates
export const usePromotionConfig = (vendor?: PromotionVendor) => {
  const { value, payload } = useFeatureValue('printCouponCodeLaunchV2');
  const [savedPromoCode] = useCustomizationContext(draft => draft.config.customizations.promoCode);
  const [category] = useCustomizationContext(draft => draft.stationeryTemplateCategory);
  const { sales, isEnabled: salesEnabled } = useSalesConfig(category);
  const { currentStep } = useNavigationContext();

  const coupons: PromotionConfig[] | null = payload instanceof Array ? payload : null;
  const couponsAndSales = useMemo(() => {
    return [...(sales && salesEnabled ? sales : []), ...(coupons ? coupons : [])];
  }, [coupons, sales, salesEnabled]);

  const currentPromoConfig = useMemo(() => {
    if (typeof savedPromoCode !== 'string') {
      return;
    }

    // Ensure that the promo code is still valid
    // TODO: Add occasion checking once we add further support for occasions.
    // Currently hardcoding occasion as we do not have a standardized way of fetching it.
    // And in the card customzier, it is not even tracked.
    const product = { occasion: 'wedding', category, vendor };
    return couponsAndSales?.find(promo => isPromoValid(promo, product) && promo.code.toLowerCase() === savedPromoCode.toLowerCase());
  }, [category, couponsAndSales, savedPromoCode, vendor]);

  return {
    isEnabled: value === 'on' && currentStep === 'review',
    promotions: couponsAndSales,
    /**
     * The promotion config for the currently stored promo code.
     */
    currentPromotion: currentPromoConfig,
  };
};
